import {useCallback, useState} from 'react'
import {Pressable, type StyleProp, View, type ViewStyle} from 'react-native'
import {RichText as RichTextAPI} from '@atproto/api'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'

import {useDemoFamily} from '#/lib/demoFamily'
import {type NavigationProp} from '#/lib/routes/types'
import {
  useConfirmFamilyLink,
  useDeclineFamilyLink,
  useFamilyLinkPreviewQuery,
} from '#/state/queries/family'
import {useSession} from '#/state/session'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {Logo} from '#/view/icons/Logo'
import {Logotype} from '#/view/icons/Logotype'
import {FamilyDeclineDialog} from '#/screens/Settings/components/FamilyDeclineDialog'
import {atoms as a, useTheme} from '#/alf'
import * as Dialog from '#/components/Dialog'
import {Check_Stroke2_Corner0_Rounded as CheckIcon} from '#/components/icons/Check'
import {ChevronRight_Stroke2_Corner0_Rounded as ChevronRightIcon} from '#/components/icons/Chevron'
import {CircleCheck_Stroke2_Corner0_Rounded as CircleCheckIcon} from '#/components/icons/CircleCheck'
import {CircleInfo_Stroke2_Corner0_Rounded as CircleInfoIcon} from '#/components/icons/CircleInfo'
import {LinearGradientBackground} from '#/components/LinearGradientBackground'
import {Loader} from '#/components/Loader'
import {RichText} from '#/components/RichText'
import * as Toast from '#/components/Toast'
import {Text} from '#/components/Typography'

/**
 * Family-invite MESSAGE: one component that is the message itself — the
 * regular chat bubble with the sender's full text (the /family/:code link
 * stays visible and tappable inside the text) and, attached below it inside
 * the SAME bubble, the invite metadata: a flush gradient cover (avatar, name,
 * @handle, role chip, headline) and the action footer.
 *
 * The action footer is the CTA sheet: the receiver can Accept or Decline
 * (declining opens a reason picker whose reply is sent back as a kind
 * message, mirroring accept). Sender, expired and family-off states each have
 * their own calm footer.
 *
 * Used by the real chat MessageItem and the demo conversation, so the message
 * text and the card always travel together on every surface.
 */
export function MessageFamilyInvite({
  code,
  messageText,
  messageFacets,
  isFromSelf,
}: {
  code: string
  /** The full message text — the invitation link stays in view. */
  messageText?: string
  /** Facets of the original message (renders the link tappable). */
  messageFacets?: unknown[]
  /** Align/styling side. Defaults to own-invite detection once resolved. */
  isFromSelf?: boolean
}) {
  const {_} = useLingui()
  const t = useTheme()
  const {currentAccount, hasSession} = useSession()
  const demo = useDemoFamily()
  const navigation = useNavigation<NavigationProp>()
  const {data: preview, isLoading, isError} = useFamilyLinkPreviewQuery(code)
  const confirm = useConfirmFamilyLink()
  const decline = useDeclineFamilyLink()
  const declineDialogControl = Dialog.useDialogControl()
  // Keep the card in a confirmed state after an accept/decline so real-chat
  // messages don't offer the buttons again.
  const [justAccepted, setJustAccepted] = useState(false)
  const [justDeclined, setJustDeclined] = useState(false)

  const fromSelf = isFromSelf ?? preview?.initiatorDid === currentAccount?.did

  const rt = new RichTextAPI({
    text: messageText ?? '',
    facets: messageFacets as RichTextAPI['facets'],
  })
  const hasText = rt.text.length > 0

  const onAccept = useCallback(() => {
    confirm.mutate(code, {
      onSuccess: () => {
        setJustAccepted(true)
        Toast.show(_(msg`Family linked on Taderok! 🎉`), {type: 'success'})
        if (demo.enabled) {
          // Give the success footer a beat, then land on the Family screen so
          // the newly linked (simulated) member is visible.
          setTimeout(() => navigation.navigate('FamilySettings'), 600)
        }
      },
      onError: e => {
        const err = (e as {error?: string}).error
        if (err === 'LinkRequestNotFound' || err === 'LinkRequestExpired') {
          Toast.show(_(msg`This invitation is invalid or has expired.`), {
            type: 'error',
          })
        } else if (err === 'ParentLimitReached') {
          Toast.show(_(msg`This learner already has 2 linked parents.`), {
            type: 'error',
          })
        } else if (err === 'ChildLimitReached') {
          Toast.show(_(msg`This parent already has 5 linked children.`), {
            type: 'error',
          })
        } else {
          Toast.show(_(msg`Could not accept this invitation.`), {
            type: 'error',
          })
        }
      },
    })
  }, [confirm, code, demo.enabled, navigation, _])

  const onDecline = useCallback(
    (reason: string, note?: string) => {
      decline.mutate(
        {code, reason, note},
        {
          onSuccess: () => setJustDeclined(true),
          onError: e => {
            const err = (e as {error?: string; message?: string})
            const notImplemented =
              err.error === 'MethodNotImplemented' ||
              /not implemented/i.test(err.message ?? '')
            Toast.show(
              notImplemented
                ? _(msg`Declining invitations isn't available on this server yet.`)
                : _(msg`Could not decline this invitation.`),
              {type: 'error'},
            )
          },
        },
      )
    },
    [decline, code, _],
  )

  // Past its TTL: friendly expired state instead of an action (demo mode
  // simulates this via a past expiresAt).
  const isExpired =
    preview != null &&
    preview.expiresAt != null &&
    new Date(preview.expiresAt).getTime() <= Date.now()
  // Your own invitation (the link you sent someone): you can't accept your
  // own invite — show a waiting state instead of action buttons.
  const isOwnInvite = preview?.initiatorDid === currentAccount?.did
  // Master family switch (demo): when OFF, no accept/decline is offered.
  const familyAllowed = !demo.enabled || demo.familyEnabled
  // Demo decline of the peer's invitation: recorded in the store so the card,
  // the thread and the Family screen all agree.
  const weDeclined = demo.enabled && demo.declinedReason != null && !fromSelf
  // Demo sender-side: the peer declined OUR invitation.
  const peerDeclined =
    demo.enabled && isOwnInvite && demo.peerDeclinedCause != null

  if (!hasText && !preview && !isLoading) return null

  const resolved = preview != null && !isError

  const bubbleStyle = fromSelf
    ? {backgroundColor: t.palette.primary_500}
    : {backgroundColor: t.palette.contrast_50}
  const hairline = fromSelf
    ? {borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.28)'}
    : [a.border_t, t.atoms.border_contrast_low]

  const declined = justDeclined || weDeclined
  const declineCause = demo.enabled ? (demo.declinedReason ?? undefined) : undefined

  return (
    <View
      style={[
        a.overflow_hidden,
        {borderRadius: 18},
        bubbleStyle,
        fromSelf ? a.self_end : a.self_start,
        {maxWidth: '88%', minWidth: 240},
      ]}>
      {/* 1 — the message text itself, with the invite link visible */}
      {hasText ? (
        <View style={[a.px_md, a.pt_sm, a.pb_xs]}>
          <RichText
            value={rt}
            style={[
              a.text_md,
              a.leading_snug,
              fromSelf ? {color: t.palette.white} : t.atoms.text_contrast_high,
            ]}
            interactiveStyle={a.underline}
            enableTags
            shouldProxyLinks
          />
        </View>
      ) : null}

      {/* 2 — invite metadata attached under the text, inside the same bubble */}
      {resolved ? (
        <>
          <View style={hairline} />
          <InviteMetaStrip
            profile={{
              did: preview.initiatorDid,
              handle: preview.initiator.handle,
              displayName: preview.initiator.displayName,
              avatar: preview.initiator.avatar,
            }}
            role={preview.initiatorRole}
          />

          {/* Accepted — green confirmation footer */}
          {justAccepted ? (
            <ActionRow hairline={hairline} fromSelf={fromSelf}>
              <CircleCheckIcon width={18} fill={t.palette.positive_500} />
              <Text
                style={[
                  a.text_md,
                  a.font_semi_bold,
                  fromSelf
                    ? {color: t.palette.white}
                    : {color: t.palette.positive_700},
                ]}>
                <Trans>Accepted — you're connected on Taderok</Trans>
              </Text>
            </ActionRow>
          ) : isExpired ? (
            <ActionRow hairline={hairline} fromSelf={fromSelf}>
              <CircleInfoIcon
                width={18}
                fill={fromSelf ? 'rgba(255,255,255,0.9)' : t.palette.negative_400}
              />
              <Text
                style={[
                  a.text_md,
                  fromSelf
                    ? {color: t.palette.white}
                    : {color: t.palette.negative_400},
                ]}>
                <Trans>This invitation has expired.</Trans>
              </Text>
            </ActionRow>
          ) : declined ? (
            // We declined the received invitation (with the chosen reason).
            <StatusFooter
              hairline={hairline}
              fromSelf={fromSelf}
              title={_(msg`You declined this invitation`)}
              caption={
                declineCause ? _(msg`Reason: ${declineCause}`) : undefined
              }
            />
          ) : peerDeclined ? (
            // Sender view: the recipient declined our invitation.
            <StatusFooter
              hairline={hairline}
              fromSelf={fromSelf}
              title={_(msg`They couldn't accept right now`)}
              caption={
                demo.peerDeclinedCause
                  ? _(msg`Reason: ${demo.peerDeclinedCause}`)
                  : undefined
              }
            />
          ) : !familyAllowed ? (
            // Family switch is OFF — never offer an action.
            <StatusFooter
              hairline={hairline}
              fromSelf={fromSelf}
              title={_(msg`Family invitations are turned off`)}
              caption={_(
                msg`Turn them back on in Settings › Account › Family to accept this.`,
              )}
            />
          ) : isOwnInvite ? (
            <ActionRow hairline={hairline} fromSelf={fromSelf}>
              <Loader
                size="sm"
                fill={fromSelf ? t.palette.white : t.palette.primary_700}
              />
              <Text
                style={[
                  a.text_md,
                  fromSelf
                    ? {color: 'rgba(255,255,255,0.92)'}
                    : t.atoms.text_contrast_medium,
                ]}>
                <Trans>Waiting for them to accept…</Trans>
              </Text>
            </ActionRow>
          ) : hasSession ? (
            // CTA sheet: Accept + Decline, both deliberate rounded actions.
            <View
              style={[
                a.px_md,
                a.py_sm,
                a.gap_sm,
                hairline,
                {
                  backgroundColor: fromSelf
                    ? 'rgba(255,255,255,0.10)'
                    : t.palette.contrast_25,
                },
              ]}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={_(msg`Accept invitation`)}
                accessibilityHint={_(msg`Links your family on Taderok`)}
                accessibilityState={{
                  disabled: confirm.isPending,
                  busy: confirm.isPending,
                }}
                disabled={confirm.isPending}
                onPress={onAccept}
                style={({pressed}) => [
                  a.flex_row,
                  a.align_center,
                  a.justify_center,
                  a.gap_xs,
                  {
                    minHeight: 46,
                    borderRadius: 23,
                    backgroundColor: t.palette.primary_500,
                  },
                  (pressed || confirm.isPending) && {opacity: 0.85},
                ]}>
                {confirm.isPending ? (
                  <Loader size="sm" fill={t.palette.white} />
                ) : (
                  <CheckIcon width={19} fill={t.palette.white} />
                )}
                <Text
                  style={[
                    a.text_md,
                    a.font_bold,
                    {color: t.palette.white, letterSpacing: 0.2},
                  ]}>
                  {confirm.isPending ? (
                    <Trans>Accepting…</Trans>
                  ) : (
                    <Trans>Accept invitation</Trans>
                  )}
                </Text>
              </Pressable>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={_(msg`Decline invitation`)}
                accessibilityHint={_(
                  msg`Lets the sender know you can't accept right now`,
                )}
                disabled={decline.isPending}
                onPress={() => declineDialogControl.open()}
                style={({pressed}) => [
                  a.flex_row,
                  a.align_center,
                  a.justify_center,
                  a.border,
                  {
                    minHeight: 44,
                    borderRadius: 22,
                    borderColor: t.atoms.border_contrast_high.borderColor,
                    backgroundColor: 'transparent',
                  },
                  pressed && t.atoms.bg_contrast_25,
                ]}>
                <Text
                  style={[
                    a.text_md,
                    a.font_semi_bold,
                    t.atoms.text_contrast_medium,
                  ]}>
                  <Trans>Decline</Trans>
                </Text>
              </Pressable>
            </View>
          ) : (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={_(msg`See invitation`)}
              accessibilityHint={_(msg`Opens the invitation details`)}
              onPress={() => navigation.navigate('FamilyInvite', {code})}
              style={({pressed}) => [
                a.flex_row,
                a.align_center,
                a.justify_center,
                a.gap_xs,
                a.border_t,
                t.atoms.border_contrast_low,
                {minHeight: 50, backgroundColor: t.palette.contrast_25},
                pressed && t.atoms.bg_contrast_25,
              ]}>
              <Text
                style={[
                  a.text_md,
                  a.font_semi_bold,
                  {color: t.palette.primary_600},
                ]}>
                <Trans>See invitation</Trans>
              </Text>
              <ChevronRightIcon width={18} fill={t.palette.primary_600} />
            </Pressable>
          )}
        </>
      ) : isLoading ? (
        <View style={[a.py_md, a.align_center]}>
          <Loader
            size="sm"
            fill={fromSelf ? t.palette.white : t.palette.primary_700}
          />
        </View>
      ) : null}

      {resolved && !fromSelf ? (
        <FamilyDeclineDialog
          control={declineDialogControl}
          peerName={preview.initiator.displayName}
          isPending={decline.isPending}
          onConfirm={onDecline}
        />
      ) : null}
    </View>
  )
}

/**
 * Calm two-line status footer (title + optional caption) for non-action
 * states: declined, peer-declined, family-off. Reads clearly at a glance
 * without ever suggesting an action that isn't available.
 */
function StatusFooter({
  title,
  caption,
  hairline,
  fromSelf,
}: {
  title: string
  caption?: string
  hairline: StyleProp<ViewStyle>
  fromSelf: boolean
}) {
  const t = useTheme()
  const fg = fromSelf ? 'rgba(255,255,255,0.92)' : t.atoms.text_contrast_high.color
  const sub = fromSelf ? 'rgba(255,255,255,0.78)' : t.atoms.text_contrast_medium.color
  return (
    <View
      style={[
        a.px_md,
        a.py_sm,
        a.gap_2xs,
        a.align_center,
        hairline,
        {
          backgroundColor: fromSelf
            ? 'rgba(255,255,255,0.10)'
            : t.palette.contrast_25,
        },
      ]}>
      <View style={[a.flex_row, a.align_center, a.justify_center, a.gap_xs]}> 
        <CircleInfoIcon width={17} fill={fromSelf ? fg : t.palette.primary_600} />
        <Text style={[a.text_md, a.font_semi_bold, {color: fg}]}>
          {title}
        </Text>
      </View>
      {caption ? (
        <Text style={[a.text_sm, {color: sub}]}>{caption}</Text>
      ) : null}
    </View>
  )
}

/**
 * Status footer row under the metadata — keeps the card's bottom edge calm and
 * evenly tall while the primary Accept action stays the loudest element.
 */
function ActionRow({
  children,
  hairline,
  fromSelf,
}: {
  children: React.ReactNode
  hairline: StyleProp<ViewStyle>
  fromSelf: boolean
}) {
  const t = useTheme()
  return (
    <View
      style={[
        a.flex_row,
        a.align_center,
        a.justify_center,
        a.gap_xs,
        a.px_md,
        hairline,
        {
          minHeight: 50,
          backgroundColor: fromSelf ? 'rgba(255,255,255,0.10)' : t.palette.contrast_25,
        },
      ]}>
      {children}
    </View>
  )
}

/**
 * Flush metadata strip under the message text — NOT a rounded card: the
 * gradient runs edge-to-edge inside the bubble (squared, clipped by the
 * bubble radius) and mirrors the big invite card's composition: avatar on
 * top, then name, @handle, role chip, headline and the Taderok logo.
 *
 * The strip grows with its content (minHeight guards the narrowest bubbles)
 * and shares the full invite card's rhythm — comfortable gaps and padding so
 * the stack fills the cover instead of huddling in the middle of a fixed
 * 2:1 slab.
 */
function InviteMetaStrip({
  profile,
  role,
}: {
  profile: {
    did: string
    handle: string
    displayName?: string
    avatar?: string
  }
  role: 'parent' | 'child'
}) {
  const name = profile?.displayName || profile?.handle || profile?.did || ''
  const headline =
    role === 'child' ? 'asks you to be their parent' : 'invited you to join their family'
  const chip = role === 'child' ? 'Learner' : 'Parent'
  return (
    <LinearGradientBackground
      gradient="sky"
      style={[
        a.align_center,
        a.justify_center,
        a.gap_sm,
        a.px_lg,
        a.py_md,
        // The cover grows with its content; the minHeight guards the
        // narrowest bubbles so the stack never clips.
        {minHeight: 170},
      ]}>
      {profile?.avatar ? (
        <UserAvatar avatar={profile.avatar} size={48} type="user" />
      ) : (
        <View
          style={[
            {
              width: 48,
              height: 48,
              borderRadius: 24,
              backgroundColor: 'rgba(255,255,255,0.35)',
            },
            a.align_center,
            a.justify_center,
          ]}>
          <Text style={[a.text_lg, a.font_bold, {color: 'white'}]}>
            {(name[0] || '?').toUpperCase()}
          </Text>
        </View>
      )}
      <Text
        style={[a.text_lg, a.font_semi_bold, a.text_center, {color: 'white'}]}
        numberOfLines={1}>
        {name}
      </Text>
      {profile?.handle ? (
        <Text
          style={[a.text_md, a.text_center, {color: 'rgba(255,255,255,0.85)'}]}
          numberOfLines={1}>
          @{profile.handle}
        </Text>
      ) : null}
      <View
        style={[
          a.rounded_full,
          {
            backgroundColor: 'rgba(255,255,255,0.25)',
            paddingHorizontal: 10,
            paddingVertical: 3,
          },
        ]}>
        <Text
          style={[
            a.text_xs,
            a.font_semi_bold,
            {
              color: 'white',
              letterSpacing: 0.5,
              textTransform: 'uppercase',
            },
          ]}>
          {chip}
        </Text>
      </View>
      <Text
        style={[
          a.text_md,
          a.text_center,
          a.leading_snug,
          {color: 'rgba(255,255,255,0.95)'},
        ]}>
        {headline}
      </Text>
      <View style={[a.flex_row, a.align_center, {gap: 6, marginTop: 2}]}>
        <Logo width={18} fill="white" />
        <Logotype width={82} fill="white" />
      </View>
    </LinearGradientBackground>
  )
}
